import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { createGame } from '../api';
import GameCreationMagic from './GameCreationMagic';
import PaymentModal from './PaymentModal';
import './MagicPage.css';

const MagicPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading, isAuthRequired, isAuthAvailable } = useAuth();
  
  const [playerName, setPlayerName] = useState('');
  const [redirectTo, setRedirectTo] = useState('/');
  const [showMagic, setShowMagic] = useState(false);
  const [gameCreationPromise, setGameCreationPromise] = useState<Promise<any> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [magicStarted, setMagicStarted] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  // Получаем параметры из URL
  useEffect(() => {
    const name = searchParams.get('playerName');
    const redirect = searchParams.get('redirectTo');
    
    if (name) {
      setPlayerName(name);
    }
    if (redirect) {
      setRedirectTo(redirect);
    }
  }, [searchParams]);

  // Проверяем авторизацию
  useEffect(() => {
    console.log('🔄 MagicPage useEffect:', { loading, user: !!user, isAuthRequired, playerName, magicStarted });

    if (loading) return;

    // Даем время AuthContext восстановить пользователя из localStorage
    const checkAuth = () => {
      if (isAuthRequired && !user) {
        // Если нужна авторизация, но пользователь не авторизован
        console.log('❌ Auth required but no user, redirecting to /auth');
        navigate('/auth');
        return;
      }

      if (!playerName) {
        // Если нет имени игрока И нет параметра в URL - возвращаемся назад
        const urlPlayerName = searchParams.get('playerName');
        if (!urlPlayerName) {
          console.log('❌ No player name in state or URL, redirecting to:', redirectTo);
          navigate(redirectTo);
          return;
        } else {
          // URL параметр есть, но state ещё не обновился - ждём следующего рендера
          console.log('⏳ Waiting for playerName to be set from URL params');
          return;
        }
      }

      // Запускаем магию только один раз
      if (!magicStarted) {
        console.log('✅ Starting magic for first time');
        setMagicStarted(true);
        startMagic();
      } else {
        console.log('⏭️ Magic already started, skipping');
      }
    };

    // Проверяем только один раз, без дополнительной задержки
    checkAuth();
  }, [loading, user, isAuthRequired, playerName, redirectTo, searchParams]);

  const startMagic = async () => {
    console.log('🎭 startMagic called for player:', playerName);
    try {
      setShowMagic(true);
      
      // Создаем игру в фоне
      console.log('🎮 Creating game...');
      const gamePromise = createGame(playerName);
      setGameCreationPromise(gamePromise);
      
    } catch (err) {
      console.error('Error starting magic:', err);
      setError('Ошибка при создании игры');
    }
  };

  const handleMagicComplete = async () => {
    if (gameCreationPromise) {
      try {
        const newGame = await gameCreationPromise;
        // Редиректим на готовую игру
        navigate(`/?gameId=${newGame.game_id}`);
      } catch (err: any) {
        console.error('Error completing magic:', err);

        // Проверяем на ошибку лимита бесплатных игр
        if (err?.status === 403 && err?.message?.includes('FREE_GAMES_LIMIT_REACHED')) {
          console.log('🔒 Free games limit reached, showing payment modal');
          setShowMagic(false);
          setShowPaymentModal(true);
          return;
        }

        setError('Ошибка при создании игры');
        setShowMagic(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="magic-page-loading">
        <div className="loading-spinner"></div>
        <p>Загружаем магию...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="magic-page-error">
        <h2>Ошибка</h2>
        <p>{error}</p>
        <button onClick={() => navigate(redirectTo)}>
          Вернуться назад
        </button>
      </div>
    );
  }

  const handlePaymentSuccess = async () => {
    console.log('💰 Payment successful, retrying game creation');
    setShowPaymentModal(false);

    try {
      // После успешной оплаты пытаемся создать игру снова
      setShowMagic(true);
      const gamePromise = createGame(playerName);
      setGameCreationPromise(gamePromise);
    } catch (err) {
      console.error('Error retrying game creation:', err);
      setError('Ошибка при создании игры после оплаты');
    }
  };

  const handlePaymentCancel = () => {
    console.log('❌ Payment cancelled, redirecting back');
    setShowPaymentModal(false);
    navigate(redirectTo);
  };

  return (
    <div className="magic-page">
      {showMagic && gameCreationPromise && (
        <GameCreationMagic
          onComplete={handleMagicComplete}
          gameCreationPromise={gameCreationPromise}
          playerName={playerName}
        />
      )}

      {showPaymentModal && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={handlePaymentCancel}
          onPaymentSuccess={handlePaymentSuccess}
        />
      )}
    </div>
  );
};

export default MagicPage;
